/**
 * Flappy Drone — the whole game, as pure data.
 *
 * A quadcopter holds a fixed x and falls; a flap pushes it up; scaffolding and
 * crane loads scroll past with a gap to thread. Nothing here touches the DOM,
 * a canvas, or a clock: `step` takes a delta and returns the next state, so the
 * rules can be exercised in a plain node test and the renderer stays a pure
 * function of whatever state it is handed.
 *
 * Coordinates are in world units — the canvas scales them, so 1 unit is 1 CSS
 * pixel at the default size and the tuning below reads in those terms.
 */

/** World size. The canvas is drawn at this aspect and scaled to fit. */
export const WORLD_WIDTH = 440;
export const WORLD_HEIGHT = 180;

/** Ground line — everything below it is the site floor, not flyable. */
export const GROUND_Y = WORLD_HEIGHT - 16;

/** The drone never moves horizontally; the site scrolls past it. */
export const DRONE_X = 62;
export const DRONE_WIDTH = 24;
export const DRONE_HEIGHT = 10;

const START_Y = WORLD_HEIGHT / 2 - DRONE_HEIGHT / 2;

const GRAVITY = 470;
const FLAP_VELOCITY = -158;
const MAX_FALL_SPEED = 260;

const BASE_SCROLL_SPEED = 78;
const SCROLL_SPEED_PER_POINT = 2.2;
const MAX_SCROLL_SPEED = 150;

export const OBSTACLE_WIDTH = 22;
const OBSTACLE_SPACING = 132;
const FIRST_OBSTACLE_X = WORLD_WIDTH + 60;
const START_GAP = 74;
const MIN_GAP = 54;
const GAP_SHRINK_PER_POINT = 0.9;
/** Keep every gap clear of the ceiling and the ground by this much. */
const GAP_MARGIN = 20;

export type ObstacleKind = "scaffold" | "crane" | "beam";

export type Obstacle = {
  /** Left edge, in world units. Decreases as the site scrolls. */
  x: number;
  /** Top of the flyable gap. */
  gapY: number;
  gapH: number;
  /** Silhouette only — every kind has the same hitboxes. */
  kind: ObstacleKind;
  /** Whether this one has already been counted towards the score. */
  passed: boolean;
};

export type GameState = {
  phase: "idle" | "playing" | "over";
  drone: { y: number; vy: number };
  obstacles: Obstacle[];
  score: number;
  /** Seconds of game time, for the renderer's rotor spin. */
  elapsed: number;
  /** World units travelled, so the ground and clouds scroll with the site. */
  distance: number;
  /** Mulberry32 state — carried in the state so runs are reproducible. */
  seed: number;
};

/** One step of mulberry32, split so callers thread the seed explicitly. */
function nextRandom(seed: number): { value: number; seed: number } {
  const advanced = (seed + 0x6d_2b_79_f5) | 0;
  let t = advanced;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return { value: ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296, seed: advanced };
}

export function createInitialState(seed = 1): GameState {
  return {
    phase: "idle",
    drone: { y: START_Y, vy: 0 },
    obstacles: [],
    score: 0,
    elapsed: 0,
    distance: 0,
    seed: seed | 0,
  };
}

/** Scroll speed and gap size both tighten with the score, to a floor/ceiling. */
export function scrollSpeed(score: number): number {
  return Math.min(
    BASE_SCROLL_SPEED + score * SCROLL_SPEED_PER_POINT,
    MAX_SCROLL_SPEED
  );
}

function gapHeight(score: number): number {
  return Math.max(MIN_GAP, START_GAP - score * GAP_SHRINK_PER_POINT);
}

const OBSTACLE_KINDS: ObstacleKind[] = ["scaffold", "crane", "beam"];

/**
 * Append one obstacle behind the right edge, spaced a fixed distance from the
 * last one so the rhythm does not depend on the frame rate.
 */
export function spawnObstacle(state: GameState): GameState {
  const last = state.obstacles.at(-1);
  const x = last ? last.x + OBSTACLE_SPACING : FIRST_OBSTACLE_X;

  const gapH = gapHeight(state.score);
  const place = nextRandom(state.seed);
  const pick = nextRandom(place.seed);
  const travel = GROUND_Y - gapH - GAP_MARGIN * 2;
  const gapY = GAP_MARGIN + place.value * Math.max(travel, 0);
  const kind =
    OBSTACLE_KINDS[Math.floor(pick.value * OBSTACLE_KINDS.length)] ??
    "scaffold";

  return {
    ...state,
    seed: pick.seed,
    obstacles: [...state.obstacles, { x, gapY, gapH, kind, passed: false }],
  };
}

/** The drone's hitbox — deliberately tighter than the drawn silhouette. */
export function droneBox(state: GameState) {
  return {
    x: DRONE_X,
    y: state.drone.y,
    w: DRONE_WIDTH,
    h: DRONE_HEIGHT,
  };
}

function overlaps(
  box: { x: number; y: number; w: number; h: number },
  rect: { x: number; y: number; w: number; h: number }
): boolean {
  return (
    box.x < rect.x + rect.w &&
    box.x + box.w > rect.x &&
    box.y < rect.y + rect.h &&
    box.y + box.h > rect.y
  );
}

/** The two solid rects of an obstacle: one hanging, one standing. */
export function obstacleRects(obstacle: Obstacle) {
  const gapBottom = obstacle.gapY + obstacle.gapH;
  return [
    { x: obstacle.x, y: 0, w: OBSTACLE_WIDTH, h: obstacle.gapY },
    {
      x: obstacle.x,
      y: gapBottom,
      w: OBSTACLE_WIDTH,
      h: GROUND_Y - gapBottom,
    },
  ];
}

export function checkCollision(state: GameState): boolean {
  const box = droneBox(state);
  if (box.y <= 0 || box.y + box.h >= GROUND_Y) {
    return true;
  }
  return state.obstacles.some((obstacle) =>
    obstacleRects(obstacle).some((rect) => overlaps(box, rect))
  );
}

/**
 * A press. Starts the run from idle, lifts the drone while playing, and starts
 * a fresh one from game over — carrying the seed on, so a retry is a new site
 * rather than the same one again.
 *
 * Nothing here debounces the press that caused the crash: game time stops with
 * the wreck, so only the caller has a clock that is still running.
 */
export function flap(state: GameState): GameState {
  if (state.phase === "over") {
    return flap({ ...createInitialState(state.seed), phase: "idle" });
  }

  return {
    ...state,
    phase: "playing",
    drone: { ...state.drone, vy: FLAP_VELOCITY },
  };
}

/**
 * Advance by `dt` seconds. Idle only ticks the clock — the drone hangs where
 * it is until the first press. After a crash the drone keeps falling so it
 * lands on the site floor, but the site itself stops scrolling.
 */
export function step(state: GameState, dt: number): GameState {
  const elapsed = state.elapsed + dt;

  if (state.phase === "idle") {
    return { ...state, elapsed };
  }

  const vy = Math.min(state.drone.vy + GRAVITY * dt, MAX_FALL_SPEED);

  if (state.phase === "over") {
    const resting = GROUND_Y - DRONE_HEIGHT;
    return {
      ...state,
      elapsed,
      drone: { y: Math.min(state.drone.y + vy * dt, resting), vy },
    };
  }

  const speed = scrollSpeed(state.score);
  let next: GameState = {
    ...state,
    elapsed,
    distance: state.distance + speed * dt,
    drone: { y: state.drone.y + vy * dt, vy },
    obstacles: state.obstacles
      .map((obstacle) => ({ ...obstacle, x: obstacle.x - speed * dt }))
      .filter((obstacle) => obstacle.x + OBSTACLE_WIDTH > -OBSTACLE_WIDTH),
  };

  const last = next.obstacles.at(-1);
  if (!last || last.x <= WORLD_WIDTH - OBSTACLE_SPACING) {
    next = spawnObstacle(next);
  }

  let scored = 0;
  next.obstacles = next.obstacles.map((obstacle) => {
    if (!obstacle.passed && obstacle.x + OBSTACLE_WIDTH < DRONE_X) {
      scored += 1;
      return { ...obstacle, passed: true };
    }
    return obstacle;
  });
  next.score += scored;

  if (checkCollision(next)) {
    return {
      ...next,
      phase: "over",
      drone: {
        y: Math.min(next.drone.y, GROUND_Y - DRONE_HEIGHT),
        vy: Math.max(next.drone.vy, 0),
      },
    };
  }

  return next;
}

/**
 * Whether the scene still has something to draw next frame. Idle and a settled
 * wreck are single static frames, so the caller can stop its rAF loop rather
 * than burn a frame budget on an error page.
 */
export function isAnimating(state: GameState): boolean {
  if (state.phase === "playing") {
    return true;
  }
  return state.phase === "over" && state.drone.y < GROUND_Y - DRONE_HEIGHT;
}
